/**
 * `<InlineTokenModal>` — the click-on-decorator modal shown when a user
 * taps a `#hashtag` or entity chip in the rich-text editor.
 *
 * Two surface shapes via the `kind` prop:
 *   - `'hashtag'` — title shows the literal `#name`, primary CTA navigates
 *     to /search?tag=<name>
 *   - `'entity'` — title shows the entity surface text; primary CTA
 *     navigates to /search?entity=<entityId>
 *
 * Implementation note: we don't depend on @radix-ui/react-dialog — this is
 * a small, single-purpose modal and the entire surface (overlay + panel +
 * 2 buttons) fits in ~50 lines without it. If we add a second modal of
 * any complexity we should reach for Radix Dialog, but for one well-scoped
 * use we keep the dep list narrow.
 *
 * Behaviour:
 *   - Click outside the panel → close
 *   - Esc → close
 *   - Tab is trapped within the panel (focus rotates between the two
 *     buttons; Shift+Tab cycles backward)
 *   - On open, the primary CTA receives focus so Enter immediately
 *     activates it (the common case)
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export type InlineTokenKind = 'hashtag' | 'entity';

export interface InlineTokenModalProps {
  /** What kind of token was clicked — drives copy + URL builder. */
  kind: InlineTokenKind;
  /** For hashtags: the tag name (without leading `#`). For entities: the
   *  surface text the user clicked. */
  label: string;
  /** For entities: the EntityId to filter search by. Ignored when kind === 'hashtag'. */
  entityId?: string;
  /** Called when the user dismisses (Esc / outside-click / Cancel button). */
  onClose: () => void;
}

export function InlineTokenModal({
  kind,
  label,
  entityId,
  onClose,
}: InlineTokenModalProps): JSX.Element {
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  const secondaryButtonRef = useRef<HTMLButtonElement>(null);

  // Esc → close. Capture phase so we win over editor key handlers
  // (the editor's contentEditable would otherwise eat Escape).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onClose]);

  // On mount, focus the primary CTA so Enter immediately navigates.
  useEffect(() => {
    primaryButtonRef.current?.focus();
  }, []);

  const onExplore = (): void => {
    if (kind === 'hashtag') {
      navigate(`/search?tag=${encodeURIComponent(label)}`);
    } else if (entityId !== undefined && entityId.length > 0) {
      navigate(`/search?entity=${encodeURIComponent(entityId)}`);
    }
    onClose();
  };

  // Tab trap — restrict focus to our two buttons. Shift+Tab from primary
  // → secondary; Tab from secondary → primary. Without this, Tab would
  // leak into the underlying editor and the user would lose the modal's
  // focus context.
  const onKeyDownPanel = (e: React.KeyboardEvent): void => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const active = document.activeElement;
    if (active === primaryButtonRef.current) {
      secondaryButtonRef.current?.focus();
    } else {
      primaryButtonRef.current?.focus();
    }
  };

  const titleText = kind === 'hashtag' ? `#${label}` : label;
  const kindEyebrow = kind === 'hashtag' ? 'Hashtag' : 'Entity';
  const exploreLabel =
    kind === 'hashtag' ? 'Explore this tag' : 'Explore this entity';

  return (
    <div
      className="inline-token-modal__overlay"
      onClick={(e) => {
        // Clicking the overlay (NOT inside the panel) dismisses.
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Token actions for ${titleText}`}
    >
      <div
        ref={panelRef}
        className="inline-token-modal__panel"
        onKeyDown={onKeyDownPanel}
      >
        <div className="inline-token-modal__kind">{kindEyebrow}</div>
        <h2 className="inline-token-modal__title">{titleText}</h2>
        <div className="inline-token-modal__actions">
          <button
            ref={primaryButtonRef}
            type="button"
            className="btn btn--primary"
            onClick={onExplore}
          >
            {exploreLabel}
          </button>
          <button
            ref={secondaryButtonRef}
            type="button"
            className="btn"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
